import {
  Checkbox,
  ChevronRightIcon,
  DropdownOption,
  FlexBox,
  JoonUIColor,
  QuestRoutine,
  SegmentedControl2,
  SPACING,
  Switch,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { RoutineSelectButton } from "./RoutineSelectButton"
import { useDateTime } from "./useDateTime"
import { CloseButton } from "../../../../components/closeButton/CloseButton"
import { SelectRow } from "../../../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../../../components/selectRowContainer/SelectRowContainer"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { anytimeToggleOptions, StartDateOption } from "../../../../constants"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

const ANYTIME_TOGGLE_KEYS: Record<
  string,
  "QUEST_TOGGLE_ANYTIME" | "QUEST_TOGGLE_TIME_OF_DAY"
> = {
  anytime: "QUEST_TOGGLE_ANYTIME",
  "time-of-day": "QUEST_TOGGLE_TIME_OF_DAY",
}

interface DateTimeModalStoreProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useDateTimeModalStore = create<DateTimeModalStoreProps>((set) => ({
  isOpen: false,
  onOpen: () => set({ isOpen: true }),
  onClose: () => set({ isOpen: false }),
}))

export const DateTimeModal = () => {
  const {
    routine,
    repetition,
    isOngoing,
    setRoutine,
    setIsOngoing,
    setReminderTimeMap,
  } = useAddEditQuestStore()
  const { isOpen, onClose } = useDateTimeModalStore()
  const {
    selectedStartDate,
    startDateOptions,
    getRoutineString,
    setSelectedStartDate,
  } = useDateTime()
  const { t } = useTranslation()

  const hasRepetition = repetition.some((day) => day)

  const localizedAnytimeToggleOptions = anytimeToggleOptions.map((option) => ({
    ...option,
    label: t(ANYTIME_TOGGLE_KEYS[option.value as string]),
  }))

  const anytimeToggleValue =
    routine === QuestRoutine.ANYTIME
      ? localizedAnytimeToggleOptions.find(
          (option) => option.value === "anytime"
        ) || localizedAnytimeToggleOptions[0]
      : localizedAnytimeToggleOptions.find(
          (option) => option.value === "time-of-day"
        ) || localizedAnytimeToggleOptions[1]

  const onToggleAnytime = (value: DropdownOption) => {
    if (
      (value.value === QuestRoutine.ANYTIME &&
        routine === QuestRoutine.ANYTIME) ||
      (value.value === "time-of-day" && routine !== QuestRoutine.ANYTIME)
    )
      return
    if (routine === QuestRoutine.ANYTIME) setRoutine(QuestRoutine.MORNING)
    else setRoutine(QuestRoutine.ANYTIME)
    setReminderTimeMap({})
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={1000}
      backgroundType="overlay"
      size="fit-content"
    >
      <FlexBox
        direction="column"
        gap={SPACING.space4}
        style={{
          position: "relative",
          backgroundColor: JoonUIColor.background.xlightGray,
          borderRadius: SPACING.space2,
          padding: SPACING.space4,
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="bodyBold">{t("QUEST_DATE_TIME")}</Typography>

        <FlexBox direction="column" gap={SPACING.space2}>
          {!hasRepetition && (
            <button
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: SPACING.space2,
                padding: SPACING.space3,
                borderRadius: SPACING.space2,
                background: JoonUIColor.background.primaryNeutral,
                cursor: "default",
              }}
            >
              <FlexBox
                direction="row"
                gap={SPACING.space2}
                align="center"
                justify="space-between"
              >
                <Typography variant="bodySmallBold">
                  {t("QUEST_KEEP_UNTIL_COMPLETE")}
                </Typography>
                <Switch
                  checked={isOngoing}
                  onChange={() => setIsOngoing(!isOngoing)}
                  name="ongoing-quest"
                />
              </FlexBox>
              <Typography
                variant="bodyXSmall"
                color={JoonUIColor.text.secondary}
                textAlign="left"
              >
                {t("QUEST_ONGOING_HELP")}
              </Typography>
            </button>
          )}

          {!isOngoing && (
            <>
              <SelectRowContainer>
                <SelectRow hideBorder showBorderBottom>
                  <Typography
                    variant="bodySmallBold"
                    color={JoonUIColor.text.secondary}
                  >
                    {t("QUEST_TIME")}
                  </Typography>
                  <Typography
                    variant="bodySmallBold"
                    color={JoonUIColor.text.primary}
                  >
                    {getRoutineString()}
                  </Typography>
                </SelectRow>
                <FlexBox
                  direction="column"
                  align="flex-start"
                  gap={SPACING.space2}
                  wrap={false}
                  fullWidth
                  style={{
                    borderBottom:
                      routine === QuestRoutine.ANYTIME
                        ? "none"
                        : `1px solid ${JoonUIColor.border.default}`,
                    padding: SPACING.space3,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SegmentedControl2
                    options={localizedAnytimeToggleOptions}
                    value={anytimeToggleValue}
                    setValue={(value) => onToggleAnytime(value)}
                    fullWidth
                    style={{
                      border: `1px solid ${JoonUIColor.border.default}`,
                      borderRadius: SPACING.space2,
                    }}
                  />
                  {routine === QuestRoutine.ANYTIME && (
                    <Typography
                      variant="bodyXSmall"
                      color={JoonUIColor.text.secondary}
                    >
                      {t("QUEST_ANYTIME_HELP")}
                    </Typography>
                  )}
                </FlexBox>
                {routine !== QuestRoutine.ANYTIME &&
                  Object.values(QuestRoutine)
                    .slice(0, 3)
                    .map((questRoutine, index) => (
                      <RoutineSelectButton
                        key={index}
                        questRoutine={questRoutine}
                        index={index}
                      />
                    ))}
              </SelectRowContainer>

              <SelectRowContainer>
                <SelectRow hideBorder showBorderBottom>
                  <Typography
                    variant="bodySmallBold"
                    color={JoonUIColor.text.secondary}
                    textAlign="left"
                  >
                    {t("QUEST_DATE")}
                  </Typography>

                  <Typography
                    variant="bodySmallBold"
                    color={JoonUIColor.text.primary}
                    textAlign="left"
                    style={{ width: "fit-content", whiteSpace: "nowrap" }}
                  >
                    {
                      startDateOptions.find(
                        (option) => option.value === selectedStartDate
                      )?.label
                    }
                  </Typography>
                </SelectRow>
                {startDateOptions.map((option, index) => (
                  <SelectRow
                    key={index}
                    hideBorder
                    showBorderBottom={index !== startDateOptions.length - 1}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedStartDate(option.value as number)
                    }}
                  >
                    <Typography variant="bodySmallBold" textAlign="left">
                      {option.label}
                    </Typography>

                    {index === startDateOptions.length - 1 &&
                    selectedStartDate !== StartDateOption.CUSTOM ? (
                      <ChevronRightIcon
                        color={JoonUIColor.icon.neutral}
                        size={24}
                      />
                    ) : (
                      <Checkbox
                        selected={option.value === selectedStartDate}
                        inputType="radio"
                        name={option.value?.toString() || "none"}
                        onChange={() => {}}
                        hideBorder
                      />
                    )}
                  </SelectRow>
                ))}
              </SelectRowContainer>
            </>
          )}
        </FlexBox>
      </FlexBox>
    </SlideUpModal>
  )
}
